<template>
	<SimulatorSheet :stepNum="stepNum" :stepName="stepName">
		<div class="simulator__grid simulator__grid--1-2">
			<div>
				<div class="sheet__controlTitle mb-20">
					{{ $t("Carga distribuída") }}
				</div>
				<ImageLoadsDistributed
					class="sheet__image"
					:isSpanBySpan="
						values.loads.permanent.distributed.cfgMode.isSpanBySpan
					"
				/>
			</div>
			<div>
				<div
					class="flex mb-40"
					:style="{
						gap: '16px',
					}"
				>
					<BToggle
						v-model="values.loads.permanent.distributed.cfgMode.isMinMax"
					>
						{{ $t("Min / Máx") }}
					</BToggle>
					<BToggle
						v-model="values.loads.permanent.distributed.cfgMode.isSpanBySpan"
					>
						{{ $t("Valor por vão") }}
					</BToggle>
				</div>
			</div>
		</div>

		<!-- IS_NOT minMax && IS_NOT spanBySpan -->
		<div
			class="simulator__grid simulator__grid--1-2"
			v-if="
				!values.loads.permanent.distributed.cfgMode.isMinMax &&
					!values.loads.permanent.distributed.cfgMode.isSpanBySpan
			"
		>
			<data value=""></data>
			<div class="grid--4 align-items-end">
				<BFieldBlock :label="$t('Número de vãos')">
					<BFieldNumber
						:decimals="false"
						:step-btns="true"
						:disabled="true"
						:value="values.spans.main.length"
						:step="1"
						:precision="0"
					/>
				</BFieldBlock>

				<BFieldBlock :label="$t('Carga distribuída')">
					<BFieldNumber
						v-model="values.loads.permanent.distributed[sampleSpanType][0].min"
						suffix="kN/m2"
						:min="0"
						:precision="2"
						:step="0.01"
					/>
				</BFieldBlock>
			</div>
		</div>

		<!-- IS minMax && IS_NOT spanBySpan -->
		<div
			class="simulator__grid simulator__grid--1-2"
			v-else-if="
				values.loads.permanent.distributed.cfgMode.isMinMax &&
					!values.loads.permanent.distributed.cfgMode.isSpanBySpan
			"
		>
			<div></div>
			<div>
				<div class="grid--4 mb-20">
					<div
						class="grid--2"
						:style="{
							gridColumn: '2 / span 2',
							transform: 'translateX(38px)',
						}"
					>
						<div class="color-neutral-600 fsz-l ml-field-label">
							{{ $t("Mínimo") }}
						</div>
						<div class="color-neutral-600 fsz-l ml-field-label">
							{{ $t("Máximo") }}
						</div>
					</div>
				</div>
				<div class="grid--4">
					<BFieldBlock :label="$t('Número de vãos')">
						<BFieldNumber
							:decimals="false"
							:step-btns="true"
							:disabled="true"
							:value="values.spans.main.length"
							:step="1"
							:precision="0"
						/>
					</BFieldBlock>

					<div
						class="grid--2"
						:style="{
							gridColumnEnd: 'span 2',
							transform: 'translateX(38px)',
						}"
					>
						<BFieldBlock :label="$t('Carga distribuída')">
							<BFieldNumber
								v-model="
									values.loads.permanent.distributed[sampleSpanType][0].min
								"
								:min="0"
								suffix="kN/m2"
								:precision="2"
								:step="0.01"
							/>
						</BFieldBlock>

						<BFieldBlock :label="$t('Carga distribuída')">
							<BFieldNumber
								v-model="
									values.loads.permanent.distributed[sampleSpanType][0].max
								"
								:min="0"
								suffix="kN/m2"
								:precision="2"
								:step="0.01"
							/>
						</BFieldBlock>
					</div>
				</div>
			</div>
		</div>

		<!-- IS_NOT minMax && IS spanBySpan -->
		<div
			class="simulator__grid simulator__grid--1"
			v-else-if="
				!values.loads.permanent.distributed.cfgMode.isMinMax &&
					values.loads.permanent.distributed.cfgMode.isSpanBySpan
			"
		>
			<SimulatorSpanMappedValuesEditor
				:values="values.loads.permanent.distributed"
				targetKey="min"
				:includeConsolesInBulkEdit="false"
				:fieldProps="{
					suffix: 'kN/m2',
					min: 0,
					precision: 2,
					step: 0.01,
				}"
			/>
		</div>

		<!-- IS minMax && IS spanBySpan -->
		<div
			class="simulator__grid simulator__grid--1"
			v-else-if="
				values.loads.permanent.distributed.cfgMode.isMinMax &&
					values.loads.permanent.distributed.cfgMode.isSpanBySpan
			"
		>
			<p class="color-neutral-600 fsz-l">{{ $t("Mínimo") }}</p>
			<SimulatorSpanMappedValuesEditor
				class="mb-40"
				:values="values.loads.permanent.distributed"
				targetKey="min"
				:includeConsolesInBulkEdit="false"
				consoleLabelPlacement="field"
				:fieldProps="{
					suffix: 'kN/m2',
					min: 0,
					precision: 2,
					step: 0.01,
				}"
			/>

			<p class="color-neutral-600 fsz-l">{{ $t("Máximo") }}</p>
			<SimulatorSpanMappedValuesEditor
				:values="values.loads.permanent.distributed"
				targetKey="max"
				:includeConsolesInBulkEdit="false"
				:fieldProps="{
					suffix: 'kN/m2',
					min: 0,
					precision: 2,
					step: 0.01,
				}"
			/>
		</div>

		<div class="sheet__divider"></div>

		<div class="simulator__grid simulator__grid--1-2">
			<div>
				<div class="sheet__controlTitle mb-20">{{ $t("Carga pontual") }}</div>
				<ImageLoadsPoint class="sheet__image" />
			</div>
			<div>
				<div class="mb-60">
					<div
						v-for="(item, idx) in values.loads.permanent.point"
						:key="idx"
						class="mb-80"
					>
						<div
							class="mb-40 flex align-items-center"
							:style="{
								gap: '20px',
							}"
						>
							<BSelector
								v-model="item.targetSpans"
								:multiple="true"
								:options="spanDescriptions"
							></BSelector>

							<BBtn
								v-if="idx !== 0"
								size="m"
								color="secondary"
								shape="circle"
								@click="handleDeletePermanentOcassionalItemBtnClick(idx)"
							>
								<BIcon name="Close" size="20px" />
							</BBtn>
						</div>

						<div class="grid--4">
							<BFieldBlock label="P1">
								<BFieldNumber
									v-model="item.loads[0]"
									:min="0"
									suffix="kN"
									:precision="2"
									:step="0.01"
								/>
							</BFieldBlock>

							<BFieldBlock label="P2">
								<BFieldNumber
									v-model="item.loads[1]"
									:min="0"
									suffix="kN"
									:precision="2"
									:step="0.01"
								/>
							</BFieldBlock>

							<BFieldBlock label="L1">
								<BFieldNumber
									v-model="item.loads[2]"
									:min="0"
									:max="100"
									suffix="%"
									:precision="2"
									:step="0.01"
								/>
							</BFieldBlock>

							<BFieldBlock label="L2">
								<BFieldNumber
									v-model="item.loads[3]"
									:min="0"
									:max="100"
									suffix="%"
									:precision="2"
									:step="0.01"
								/>
							</BFieldBlock>
						</div>
					</div>
				</div>

				<button
					class="sheet__bigAddBtn"
					@click="handleAddPermanentOcassionalItemBtnClick()"
				>
					{{ $t("Adicionar grupo") }}
					<BIcon name="Add"></BIcon>
				</button>
			</div>
		</div>

		<div class="sheet__footer">
			<BBtn size="l" variant="text" @click="handleRestartBtnClick">{{
				$t("Reiniciar")
			}}</BBtn>
			<BBtn size="l" @click="handleSaveBtnClick" :disabled="isSaving">{{
				$t("Salvar e Continuar")
			}}</BBtn>
		</div>
	</SimulatorSheet>
</template>
<script>
import { mapMutations, mapActions, mapState, mapGetters } from "vuex";
import { deepClone } from "@/utils";

import SimulatorSheet from "./SimulatorSheet.vue";

import BSelector from "@/components/base/BSelector.vue";
import BSelect from "@/components/base/BSelect.vue";
import BFieldBlock from "@/components/base/BFieldBlock.vue";
import BCheckbox from "@/components/base/BCheckbox.vue";
import BFieldNumber from "@/components/base/BFieldNumber.vue";
import BBtn from "@/components/base/BBtn.vue";
import BToggle from "@/components/base/BToggle.vue";
import BIcon from "@/components/base/BIcon/index.vue";

import SimulatorSpanMappedValuesEditor from "./SimulatorSpanMappedValuesEditor";

import ImageLoadsDistributed from "./Images/ImageLoadsDistributed/index.vue";
import ImageLoadsPoint from "./Images/ImageLoadsPoint.vue";

import mixinEmitLifecycleEvents from "@/mixins/emitLifecycleEvents";
import mixinObserveChanges from "@/pages/Simulator/mixins/observeChanges";

export default {
	components: {
		BSelector,
		BToggle,
		BFieldBlock,
		BCheckbox,
		BSelect,
		BFieldNumber,
		BBtn,
		BIcon,
		SimulatorSpanMappedValuesEditor,
		SimulatorSheet,
		ImageLoadsDistributed,
		ImageLoadsPoint,
	},
	mixins: [mixinEmitLifecycleEvents, mixinObserveChanges],
	props: {
		...SimulatorSheet.props,
	},
	data() {
		return {
			values: null,
			isSaving: false,
		};
	},
	computed: {
		...mapState("simulator", {
			simulationOptions: "simulationOptions",
			storeSimulation: "simulation",
		}),

		storeValues() {
			return this.storeSimulation.values;
		},
		...mapGetters("simulator", ["spanDescriptions", "isFirstStepComplete"]),
	},
	methods: {
		...mapMutations("simulator", ["setSimulationValues"]),
		...mapActions("simulator", ["save"]),

		handleSaveBtnClick() {
			this.$emit("save", this.values);
		},

		handleRestartBtnClick() {
			this.$emit("restart");
		},

		handleAddPermanentOcassionalItemBtnClick() {
			this.values.loads.permanent.point.push({
				targetSpans: [],
				loads: [0, 0, 0, 0],
			});
		},

		handleDeletePermanentOcassionalItemBtnClick(itemIdx) {
			this.values.loads.permanent.point.splice(itemIdx, 1);
		},
	},

	watch: {
		"values.loads.permanent.distributed": {
			handler(val) {
				if (!val.cfgMode.isMinMax) {
					["main", "console"].forEach((spanType) => {
						val[spanType].forEach((item) => {
							item.max = item.min;
						});
					});
				}

				if (!val.cfgMode.isSpanBySpan) {
					const sampleItem = val[this.sampleSpanType][0];

					["main", "console"].forEach((spanType) => {
						val[spanType].forEach((item) => {
							item.min = sampleItem.min;
							item.max = sampleItem.max;
						});
					});
				}
			},
			deep: true,
		},
	},

	created() {
		this.values = deepClone(this.storeValues);
		this.sampleSpanType = this.values.spans.useConsole ? "console" : "main";
	},
};
</script>

<style lang="scss">
.spansTable__smallText {
	font-size: var(--font-size-s);
	color: var(--color-neutral-500);
}

.spansTable__checkboxCell {
	display: flex;
	align-items: center;
	gap: 20px;
}

.spansTable__headCell2 {
	padding-left: 16px;
}

.spansTable__spanNumber {
	font-variant-numeric: tabular-nums;
}
</style>
